/**
 * Prompt 优化面板组件
 * 
 * 提供 Prompt 自动优化功能：
 * - 启动优化实验
 * - 查看优化进度
 * - 查看历史记录
 * - 应用/回滚 Prompt
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Play, Check, Loader2, TrendingUp, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useState, useEffect } from 'react'

interface Experiment {
  id: string
  type: string
  status: 'running' | 'completed' | 'failed'
  baselineScore: number | null
  bestScore: number | null
  improvement: number | null
  iterations: number
  createdAt: number
  completedAt: number | null
  creator?: {
    id: string
    name: string
  }
}

interface ExperimentStatus {
  id: string
  status: string
  currentIteration: number
  totalIterations: number
  baselineScore: number | null
  bestScore: number | null
  improvement: number | null
}

export function PromptOptimizerPanel() {
  const queryClient = useQueryClient()
  const [isPolling, setIsPolling] = useState(false)

  // 获取当前优化状态
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['prompt-optimization-status'],
    queryFn: async () => {
      const res = await api.get('/optimize/prompt/status')
      return res.data
    },
    refetchInterval: isPolling ? 3000 : false,
  })

  // 获取实验历史
  const { data: experimentsData, isLoading: experimentsLoading } = useQuery({
    queryKey: ['experiments'],
    queryFn: async () => {
      const res = await api.get('/optimize/experiments')
      return res.data
    },
  })

  // 启动优化
  const startMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/optimize/prompt/start', {
        maxIterations: 5,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Prompt 优化已启动')
      setIsPolling(true)
      queryClient.invalidateQueries({ queryKey: ['prompt-optimization-status'] })
    },
    onError: (error: any) => {
      toast.error(error.error || '启动失败')
    },
  })

  // 应用 Prompt
  const applyMutation = useMutation({
    mutationFn: async (experimentId: string) => {
      const res = await api.post(`/optimize/prompt/apply`, null, {
        params: { experimentId },
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Prompt 已应用')
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
    },
    onError: (error: any) => {
      toast.error(error.error || '应用失败')
    },
  })

  // 监听优化状态
  useEffect(() => {
    if (statusData?.status?.status === 'completed' || statusData?.status?.status === 'failed') {
      setIsPolling(false)
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
    }
  }, [statusData, queryClient])

  const status = statusData?.status as ExperimentStatus | null
  const experiments = experimentsData || []
  const isRunning = status?.status === 'running'

  return (
    <div className="space-y-6">
      {/* 当前状态卡片 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Prompt 优化状态</CardTitle>
              <CardDescription>自动优化翻译 Prompt 以提升翻译质量</CardDescription>
            </div>
            {isRunning && (
              <Badge variant="secondary" className="animate-pulse">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                优化中
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {statusLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isRunning ? (
            <RunningStatus status={status} />
          ) : status?.status === 'completed' ? (
            <CompletedStatus 
              status={status} 
              onApply={() => applyMutation.mutate(status.id)}
              isApplying={applyMutation.isPending}
            />
          ) : (
            <IdleStatus onStart={() => startMutation.mutate()} isStarting={startMutation.isPending} />
          )}
        </CardContent>
      </Card>

      {/* 历史记录 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">优化历史</CardTitle>
          <CardDescription>查看历史优化实验记录</CardDescription>
        </CardHeader>
        <CardContent>
          {experimentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : experiments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              暂无优化记录
            </div>
          ) : (
            <div className="space-y-3">
              {experiments.map((exp: Experiment) => (
                <ExperimentCard 
                  key={exp.id} 
                  experiment={exp}
                  onApply={() => applyMutation.mutate(exp.id)}
                  isApplying={applyMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// 空闲状态
function IdleStatus({ onStart, isStarting }: { onStart: () => void; isStarting: boolean }) {
  return (
    <div className="text-center py-8 space-y-4">
      <div className="text-muted-foreground">
        <p className="mb-2">点击下方按钮开始 Prompt 自动优化</p>
        <p className="text-xs">优化过程约需 5-10 分钟，期间系统会自动测试多个 Prompt 变体</p>
      </div>
      <Button onClick={onStart} disabled={isStarting}>
        {isStarting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            启动中...
          </>
        ) : (
          <>
            <Play className="h-4 w-4 mr-2" />
            开始优化
          </>
        )}
      </Button>
    </div>
  )
}

// 运行中状态
function RunningStatus({ status }: { status: ExperimentStatus }) {
  const progress = status.totalIterations > 0 
    ? (status.currentIteration / status.totalIterations) * 100 
    : 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">优化进度</span>
        <span className="font-medium">{status.currentIteration} / {status.totalIterations}</span>
      </div>
      
      <Progress value={progress} className="h-2" />
      
      {status.baselineScore !== null && (
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div>
            <p className="text-xs text-muted-foreground">基线分数</p>
            <p className="text-lg font-semibold">{(status.baselineScore * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">当前最佳</p>
            <p className="text-lg font-semibold text-success">
              {status.bestScore ? (status.bestScore * 100).toFixed(1) + '%' : '-'}
            </p>
          </div>
        </div>
      )}
      
      <p className="text-xs text-muted-foreground text-center">
        优化进行中，请稍候...
      </p>
    </div>
  )
}

// 完成状态
function CompletedStatus({ 
  status, 
  onApply, 
  isApplying 
}: { 
  status: ExperimentStatus
  onApply: () => void
  isApplying: boolean
}) {
  const improvement = status.improvement || 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-success">
        <Check className="h-5 w-5" />
        <span className="font-medium">优化完成</span>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">基线分数</p>
          <p className="text-lg font-semibold">{(status.baselineScore! * 100).toFixed(1)}%</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">最佳分数</p>
          <p className="text-lg font-semibold text-success">
            {(status.bestScore! * 100).toFixed(1)}%
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">提升幅度</p>
          <p className="text-lg font-semibold text-success flex items-center gap-1">
            <TrendingUp className="h-4 w-4" />
            +{improvement.toFixed(1)}%
          </p>
        </div>
      </div>
      
      <Button onClick={onApply} disabled={isApplying} className="w-full">
        {isApplying ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            应用中...
          </>
        ) : (
          <>
            <Check className="h-4 w-4 mr-2" />
            应用优化后的 Prompt
          </>
        )}
      </Button>
    </div>
  )
}

// 实验卡片
function ExperimentCard({ 
  experiment, 
  onApply, 
  isApplying 
}: { 
  experiment: Experiment
  onApply: () => void
  isApplying: boolean
}) {
  const statusConfig = {
    running: { label: '运行中', variant: 'secondary' as const, icon: Loader2 },
    completed: { label: '已完成', variant: 'default' as const, icon: Check },
    failed: { label: '失败', variant: 'destructive' as const, icon: AlertCircle },
  }

  const config = statusConfig[experiment.status]
  const Icon = config.icon

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
      <div className="flex items-center gap-3">
        <Badge variant={config.variant}>
          <Icon className={`h-3 w-3 mr-1 ${experiment.status === 'running' ? 'animate-spin' : ''}`} />
          {config.label}
        </Badge>
        
        <div>
          <p className="text-sm font-medium">
            {experiment.improvement !== null ? `+${experiment.improvement.toFixed(1)}%` : '-'}
          </p>
          <p className="text-xs text-muted-foreground">
            {experiment.iterations} 次迭代
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <div className="text-right text-xs text-muted-foreground">
          <p>{formatDate(experiment.createdAt)}</p>
        </div>
        
        {experiment.status === 'completed' && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={onApply}
            disabled={isApplying}
          >
            应用
          </Button>
        )}
      </div>
    </div>
  )
}
